import React from "react"
import _ from "lodash"
import SequenceWaveform from "./SequenceWaveform"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';
import {
  Avatar,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  IconButton,
} from "@mui/material"

import { observer, inject } from "mobx-react"
import { logEvent } from "../utils/analytics"

const SampleWaveformContainer = styled('div')`
  height: 150px;
  position: relative;
  display: block;
`

const SequenceCardAttribute = styled('li')`
  padding-right: 10px;
  color: rgb(117, 116, 116);
`

const WidgetIcon = styled('i')`
  margin-right: 0;
  font-size: 11px;
`

const CardActionButton = styled('span')`
  color: ${(props) => (props.isPlaying ? '#ee534f' : '#4db6ac')};

  &:hover {
    color: ${(props) => (props.isPlaying ? '#f38784' : '#80cbc4')};
  }
`

const FavoriteIconAndCount = styled('a')`
  display: flex;
  transition: ${(props) => (props.isFavorited ? "color .3s ease" : null)};
  color: ${(props) => (props.isFavorited ? "#E2264D" : null)};

  i {
    margin-right: 3px;
  }
`

const SFAvatar = styled(Avatar)`
  width: 25px;
  height: 25px;
`

const SFCard = styled(Card)`
  border-radius: 5px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2);
`

const SFCardActions = styled(CardActions)`
  justify-content: space-around;
`

@inject((props, stores, context) => props)
@observer
export default class SequenceCard extends React.Component {
  static defaultProps = {
    shouldRenderHeader: false,
  }

  constructor(props) {
    super(props)
    this.sequenceWaveform = React.createRef()
  }

  componentDidMount() {
    this.checkIfFavorite()
    if (typeof window !== "undefined") {
      window.addEventListener("favoritesReceivedEvent", this.checkIfFavorite)
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("favoritesReceivedEvent", this.checkIfFavorite)
    }
  }

  checkIfFavorite = () => {
    if (typeof localStorage !== "undefined") {
      const { sequence } = this.props
      const { id } = sequence
      const favoriteSequences = localStorage.getItem("favoriteSequences")
        ? localStorage.getItem("favoriteSequences")
        : []
      const isFavorited = favoriteSequences.includes(id)
      if (isFavorited) {
        sequence.setToFavorite()
      }
    }
  }

  handleActionBtnClick = () => {
    const { sequence } = this.props
    if (!sequence.isPlaying) {
      this.sequenceWaveform.current.playSoundOnce()
    } else {
      this.sequenceWaveform.current.stopSound()
    }
  }

  handleFavoriteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const { sequence } = this.props
    const { isFavorited } = sequence

    if (Helpers.isLoggedIn() == false) {
      FlashMessages.notifyUserIsLoggedOut()
      return
    }

    if (Boolean(isFavorited)) {
      sequence.unfavorite()
    } else {
      sequence.favorite()
    }
  }

  handleWaveformClick = (e) => {
    const { sequence } = this.props
    if (e.shiftKey || e.metaKey) {
      window.open(sequence.sequence_url, "_blank").focus()
    } else {
      window.location.href = sequence.sequence_url
    }
  }

  handleDownloadButtonClick = () => {
    logEvent("Download sequence click")
  }

  renderCardActionButton() {
    const { sequence } = this.props

    const iconClassname = sequence.isPlaying
      ? "fas fa-stop"
      : "fas fa-play"

    return (
      <CardActionButton
        className="card-action-button waves-effect waves-light"
        onClick={this.handleActionBtnClick}
        isPlaying={sequence.isPlaying}
        >
        <i className={iconClassname}></i>
      </CardActionButton>
    );
  }

  renderFavoriteIconAndCount() {
    const { sequence } = this.props
    const { isFavorited, favorites_count } = sequence
    const iconClassname = Boolean(isFavorited) ? "fas fa-heart" : "far fa-heart"

    return (
      <FavoriteIconAndCount
        href="#"
        onClick={this.handleFavoriteClick}
        isFavorited={Boolean(isFavorited)}
      >
        <i className={iconClassname} aria-hidden="true"></i>
        <span>{favorites_count > 0 && favorites_count}</span>
      </FavoriteIconAndCount>
    )
  }

  renderCardHeader() {
    const { sequence } = this.props
    const {
      user_avatar_url,
      user_display_name_truncated,
      user_slug
    } = sequence
    const user_path = "/users/" + user_slug

    return (

      <CardHeader
        avatar={
        <a
          css={css`
            display: flex;
          `}
          href={user_path}
        >
          <SFAvatar src={user_avatar_url} />
        </a>
        }
          title={user_display_name_truncated}
        />
    )
  }

  render() {
    const { sequence, shouldRenderHeader } = this.props
    const {
      tempo,
      mode,
      key,
      slug,
      duration,
    } = sequence

    const sequence_download_path = "/sequences/" + slug + "/download"
    const approximate_duration = Number.parseFloat(duration).toFixed(1)

    return (
      <SFCard className="hoverable">
          {shouldRenderHeader && this.renderCardHeader()}
          <SampleWaveformContainer>
            <SequenceWaveform
              sequence={sequence}
              ref={this.sequenceWaveform}
              handleWaveformClick={this.handleWaveformClick}
            />
          </SampleWaveformContainer>
          <CardContent>
            <ul css={css`
              display: flex;
              margin: 0;
            `}>
              {tempo && (
                <SequenceCardAttribute>
                  <WidgetIcon className="fas fa-ellipsis" aria-hidden="true"></WidgetIcon>{" "}
                  {tempo}bpm
                </SequenceCardAttribute>
              )}
              {key && (
                <SequenceCardAttribute>
                  <WidgetIcon className="fas fa-music" aria-hidden="true"></WidgetIcon> {key}{" "}
                  {mode}
                </SequenceCardAttribute>
              )}
              {approximate_duration && (
                <SequenceCardAttribute>
                  <WidgetIcon className="far fa-clock" aria-hidden="true"></WidgetIcon>{" "}
                  {approximate_duration}s
                </SequenceCardAttribute>
              )}
            </ul>
          </CardContent>
          <SFCardActions>
            {this.renderCardActionButton()}
            {this.renderFavoriteIconAndCount()}

            <a
              css={css`
                color: #ffab40;
                &:hover {
                  color: #ffd54f;
                }
              `}
              href={sequence_download_path}
              data-method="post"
              data-remote="true"
              rel="nofollow"
              className="download-sample-link card-action-button waves-light waves-effect"
              onClick={this.handleDownloadButtonClick}
            >
              <i className="fas fa-download"></i>
            </a>
          </SFCardActions>
      </SFCard>
    )
  }
}